/**
 * bookableSlots.js — TEST 1.35 / TEST 2.08. ONE RULE FOR WHAT A PATIENT CAN PICK.
 *
 * ★★★ THE RULE (Alex, D-b). No booking without real hours. A patient is offered a
 *   date and a time only when GET /doctors/<id>/slots returned it. When it returned
 *   nothing, the screen says WHY (the reason below) and shows no calendar — it never
 *   fills the gap with weekdays it generated or a static list of office hours. Those
 *   fallbacks were how a patient "booked" 10:30 AM with a physician whose scheduling
 *   was off, or who was on with no days, and the physician found a request for a time
 *   they had never offered.
 *
 * ★★ THE REASONS. `reason` is null exactly when `slots` is non-empty.
 *     'no-doctor'      the case names nobody, so there is no calendar to read
 *     'scheduling-off' the API said enabled:false (online scheduling is off)
 *     'no-hours'       the API said reason:'no_hours' with enabled:true (on, no days)
 *     'none-open'      hours exist, every slot in the window is taken or past
 *     'fetch-failed'   the read itself failed: NOT an absence of appointments
 *   services/api.js getAvailableSlots turns a failed fetch into
 *   { enabled:false, slots:[], fetchFailed:true }. Before `fetchFailed` existed that
 *   was indistinguishable from "scheduling is off", so a network blip told the patient
 *   to telephone the office.
 *
 * ★ TIMES ARE 'HH:MM' (24h) HERE AND IN EVERY PAYLOAD. Display formatting happens at the
 *   edge (AppointmentCalendar.formatTime / dateUtils.formatAppointmentTime). Sorting a
 *   list that was already formatted put 1:00 PM before 9:00 AM; sorting by minutes and
 *   formatting afterwards cannot.
 *
 * Pure module: no imports, so scripts/check-no-invented-slots.mjs runs it directly.
 */

export const SLOT_REASONS = ['no-doctor', 'scheduling-off', 'no-hours', 'none-open', 'fetch-failed']

/** 'HH:MM[:SS]', 'H:MM', 'h:mm AM/PM', or an ISO date-time → 'HH:MM', else null. */
export function toHHMM(value) {
  if (value == null) return null
  let s = String(value).trim()
  if (!s) return null
  const iso = s.match(/^\d{4}-\d{2}-\d{2}T(\d{1,2}:\d{2})/)
  if (iso) s = iso[1]
  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp])\.?\s*[Mm]\.?$/)
  if (ampm) {
    let h = Number(ampm[1])
    const m = Number(ampm[2])
    if (h < 1 || h > 12 || m > 59) return null
    const pm = ampm[3].toLowerCase() === 'p'
    if (h === 12) h = pm ? 12 : 0
    else if (pm) h += 12
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }
  const h24 = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  if (h24) {
    const h = Number(h24[1])
    const m = Number(h24[2])
    if (h > 23 || m > 59) return null
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }
  return null
}

/** Minutes after midnight for any form toHHMM accepts; NaN when unparsable. */
export function slotMinutes(value) {
  const hhmm = toHHMM(value)
  if (!hhmm) return NaN
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

/** Normalised, de-duplicated, sorted BY MINUTES. Unparsable entries are dropped. */
export function sortTimes(times) {
  const out = []
  for (const t of Array.isArray(times) ? times : []) {
    const hhmm = toHHMM(t)
    if (hhmm && !out.includes(hhmm)) out.push(hhmm)
  }
  return out.sort((a, b) => slotMinutes(a) - slotMinutes(b))
}

const slotDateOf = (s) => {
  if (!s) return ''
  if (typeof s === 'object') return typeof s.date === 'string' ? s.date.slice(0, 10) : ''
  if (typeof s === 'string') return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : ''
  return ''
}

/**
 * Any slot shape the API has returned — grouped {date, dayName, times[]}, flat
 * {date, time}, or ISO strings — as grouped [{date, dayName, times}] sorted by date,
 * each date's times sorted by minutes. A date with no parsable time is dropped: a date
 * the patient can tap and then find empty is an invented date.
 */
export function normalizeSlots(raw) {
  const byDate = new Map()
  for (const s of Array.isArray(raw) ? raw : []) {
    const date = slotDateOf(s)
    if (!date) continue
    const entry = byDate.get(date) || { date, dayName: (s && typeof s === 'object' && s.dayName) || '', times: [] }
    if (s && typeof s === 'object' && Array.isArray(s.times)) entry.times.push(...s.times)
    else if (s && typeof s === 'object' && s.time) entry.times.push(s.time)
    else if (typeof s === 'string' && s.includes('T')) entry.times.push(s)
    if (!entry.dayName && s && typeof s === 'object' && s.dayName) entry.dayName = s.dayName
    byDate.set(date, entry)
  }
  return [...byDate.values()]
    .map((e) => ({ ...e, times: sortTimes(e.times) }))
    .filter((e) => e.times.length > 0)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
}

/** GET /slots response → { slots, reason, duration }. See the reasons at the top.
 *  D7 (PD7-1): plus `places` (slotPlacesOf) ONLY when the answer carries `locations`; an
 *  answer without it (every API before D7) returns the very same three keys as before. */
export function classifySlotsResult(result) {
  if (!result || typeof result !== 'object' || result.fetchFailed) {
    return { slots: [], reason: 'fetch-failed', duration: null }
  }
  const duration = result.duration ?? null
  if (result.enabled === false) return { slots: [], reason: 'scheduling-off', duration }
  if (result.reason === 'no_hours') return { slots: [], reason: 'no-hours', duration }
  const slots = normalizeSlots(result.slots)
  if (slots.length === 0) return { slots: [], reason: 'none-open', duration }
  const places = slotPlacesOf(result)
  return places ? { slots, reason: null, duration, places } : { slots, reason: null, duration }
}

/**
 * ★★ D7 (PROD-639, PD7-1/2): WHERE EACH SLOT IS. From D7 on, GET /slots answers a top-level
 *   `locations` {id: {name, address, city, state, zip}} and, per day, `slotLocations`
 *   {"HH:MM": id}: the practice location the booking would be stored at (the API's
 *   _effective_location rule; the patient never picks it). `locations` is the D7 capability
 *   marker: without it this returns null and every screen shows exactly what it showed before.
 *
 *   → null, or a plain object (it lives in React state):
 *     { count, byId: {id: {name, address}}, at: {'YYYY-MM-DD HH:MM': id} }
 *   `count` is the number of the physician's locations; a slot whose id is not in
 *   `locations` has no place (nothing is invented). `address` is one line, '' when none.
 *   ★ PROD-795: the line is "street, city, ST ZIP", the shape of utils/formatAddress.js
 *   formatFullAddress (copied here, not imported: this module is import-free by contract;
 *   check-slot-location SL-7 holds the two equal). It read "1 Main St, Pasadena, CA, 91101".
 */
export function slotPlacesOf(result) {
  if (!result || typeof result !== 'object') return null
  const locs = result.locations
  if (!locs || typeof locs !== 'object' || Array.isArray(locs)) return null
  const str = (v) => (typeof v === 'string' ? v.trim() : '')
  const byId = {}
  for (const [id, loc] of Object.entries(locs)) {
    if (!loc || typeof loc !== 'object') continue
    const stateZip = [str(loc.state), str(loc.zip)].filter(Boolean).join(' ')
    byId[id] = { name: str(loc.name), address: [str(loc.address), str(loc.city), stateZip].filter(Boolean).join(', ') }
  }
  const at = {}
  for (const s of Array.isArray(result.slots) ? result.slots : []) {
    const date = slotDateOf(s)
    const map = s && typeof s === 'object' ? s.slotLocations : null
    if (!date || !map || typeof map !== 'object' || Array.isArray(map)) continue
    for (const [time, id] of Object.entries(map)) {
      const hhmm = toHHMM(time)
      if (hhmm && typeof id === 'string' && Object.prototype.hasOwnProperty.call(byId, id)) at[`${date} ${hhmm}`] = id
    }
  }
  return { count: Object.keys(byId).length, byId, at }
}

/**
 * The place of ONE slot as the patient reads it, or null (no `locations` in the answer, a
 * virtual visit, or a slot the API placed nowhere).
 *   name     only when the physician has MORE THAN ONE location ('' otherwise): with one,
 *            the name says nothing the address does not
 *   address  the location's one-line address ('' when it has none)
 * `virtual` is true for a video / telehealth visit: it happens nowhere, so no place is shown.
 */
export function slotPlace(places, date, time, virtual = false) {
  if (virtual || !places || typeof places !== 'object' || !places.at || !places.byId) return null
  const hhmm = toHHMM(time)
  if (!date || !hhmm) return null
  const id = places.at[`${date} ${hhmm}`]
  const loc = id != null ? places.byId[id] : null
  if (!loc) return null
  const name = places.count > 1 ? loc.name : ''
  if (!name && !loc.address) return null
  return { name, address: loc.address }
}

/**
 * The line under each time button: {'HH:MM': name} for the times of `date`, only when the
 * physician has more than one location and the visit is not virtual. null otherwise, and
 * null is what AppointmentCalendar renders exactly as it did before D7.
 */
export function slotPlaceNames(places, date, times, virtual = false) {
  if (virtual || !places || !(places.count > 1)) return null
  const out = {}
  for (const t of Array.isArray(times) ? times : []) {
    const p = slotPlace(places, date, t, false)
    if (p && p.name) out[t] = p.name
  }
  return Object.keys(out).length ? out : null
}

/**
 * The only way a booking screen reads a physician's slots. `api` is doctorsAPI
 * (passed in so this module stays import-free). Never throws.
 */
export async function bookableSlots(api, doctorId, days = 14, category = 'initial') {
  if (!doctorId) return { slots: [], reason: 'no-doctor', duration: null }
  try {
    return classifySlotsResult(await api.getAvailableSlots(doctorId, days, category))
  } catch {
    return { slots: [], reason: 'fetch-failed', duration: null }
  }
}

/** The dates a patient may pick: only dates that carry at least one real time. */
export function datesOf(slots) {
  return normalizeSlots(slots).map((e) => e.date)
}

/** The 'HH:MM' times for one date, sorted by minutes; [] when there are none. */
export function timesForDate(slots, date) {
  if (!date) return []
  const entry = normalizeSlots(slots).find((e) => e.date === date)
  return entry ? entry.times : []
}

/**
 * The same-day lead buffer (v225.10.0 #6, per product): on TODAY a time is offered only
 * when it is at least 60 minutes away for an in-person visit, 30 for a virtual one
 * (appointmentType 'video'). Any other date keeps every time. `now` is local time
 * (v160.1: never toISOString, which is tomorrow in western timezones).
 * P1 round 3 (H4): one rule for BOTH the time list and the date list, so today is not
 * a date when every one of its times falls inside the buffer.
 */
export function timesAfterSameDayLead(times, date, appointmentType, now = new Date()) {
  const list = Array.isArray(times) ? times : []
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  if (date !== today) return list
  const cutoff = now.getHours() * 60 + now.getMinutes() + (appointmentType === 'video' ? 30 : 60)
  return list.filter((t) => slotMinutes(t) >= cutoff)
}

/**
 * i18n keys for the "nothing to pick" card, chosen by reason. `body` may be null.
 * Existing copy is reused where it already says the right thing (caseHome.*).
 */
export function noSlotsMessageKeys(reason) {
  switch (reason) {
    case 'fetch-failed':
      return { title: 'caseHome.slotsFetchFailed', body: null }
    case 'none-open':
      return { title: 'appointment.noOpenTimesTitle', body: 'appointment.contactOfficeToArrange' }
    case 'no-doctor':
      return { title: 'caseHome.noDoctorToReschedule', body: null }
    case 'scheduling-off':
    case 'no-hours':
    default:
      return { title: 'appointment.noOnlineBookingTitle', body: 'appointment.contactOfficeToArrange' }
  }
}

/**
 * D-h (round 3, H1) — true exactly when the physician has NO REAL HOURS: online
 * scheduling is off, or on with no days. Those are the two reasons for which
 * orella-api records an observation concern without a slot (409 doctor_has_no_hours,
 * findingRecorded:true). 'none-open' is NOT one of them: that physician has hours, and
 * a report without a slot is refused there with 400 requested_slot_required.
 */
export function reasonIsNoHours(reason) {
  return reason === 'scheduling-off' || reason === 'no-hours'
}

/**
 * P1 round 5 (B1) — true when the observation report bridge sends the concern WITHOUT a
 * slot: there is nothing real to pick. No hours (above), none open in the window, or no
 * doctor the app could read a calendar for. orella-api records the concern for each:
 * 409 doctor_has_no_hours (no hours) or 409 no_open_slot (hours, no slot sent), both
 * findingRecorded:true. 'fetch-failed' is NOT one of them: the read failed, so the
 * patient retries it instead of giving up the appointment.
 */
export function reasonAllowsSendWithoutSlot(reason) {
  return reasonIsNoHours(reason) || reason === 'none-open' || reason === 'no-doctor'
}

/**
 * D-h — the observation report route's two answers that RECORD the concern and refuse
 * only the appointment (orella-api _record_concern_without_appointment):
 *   409 {code:'doctor_has_no_hours', findingRecorded:true, appointmentCreated:false}
 *       the physician has no usable hours (round 3, H1)
 *   400 {code:'no_positive_outcome', findingRecorded:true, appointmentCreated:false}
 *       the care plan defines no positive outcome, so there is no follow-up to bridge
 *       to (API round 3, G2; app round 4, L1). The API decides this BEFORE it reads the
 *       slot or the hours, so it can answer a report that carried a real slot.
 * The CONCERN arrived (the physician is notified); only the appointment was refused.
 * findingRecorded false or absent (an API that refused before writing) means nothing
 * was recorded, and the caller shows an error.
 */
//   Round 5 (B1, B3) — two more, both findingRecorded:true:
//   409 {code:'no_open_slot'} the physician has hours but the report carried no slot
//   500 {code:'appointment_not_created'} the concern was recorded and the observation
//       sealed, then the appointments insert failed; a retry would report it twice.
// THE ONE LIST. DeliverScreen reads it only through the two helpers below.
export const CONCERN_RECORDED_CODES = ['doctor_has_no_hours', 'no_positive_outcome', 'no_open_slot', 'appointment_not_created']

// PROD-1089 step 2: 409 {code:'physician_inactive'} (a deactivated physician takes no NEW visit; also
// carried as claimRefusal.code when a case save withholds a claim). Both sentences are English now; the
// Spanish is withheld for Dr. Rami (src/i18n/pendingEs.js), so a caller passes the language's `hasText`
// and under es keeps today's behaviour (never English in a Spanish app). Screens bind the language in with
// appointmentErrorKeyFor / concernSentMessageKeyFor; the one-argument names answer as for English.
export const PHYSICIAN_INACTIVE_CODE = 'physician_inactive'
export const PHYSICIAN_INACTIVE_ERR_KEY = 'appointment.errPhysicianInactive'
export const PHYSICIAN_INACTIVE_CONCERN_KEY = 'appointment.concernSentPhysicianInactive'
const textOk = (hasText, key) => typeof hasText !== 'function' || hasText(key)
/** true for a refusal body or a claimRefusal body that carries the physician_inactive code. */
export function isPhysicianInactive(err) {
  return !!err && typeof err === 'object' && (err.code === PHYSICIAN_INACTIVE_CODE || err.claimRefusal?.code === PHYSICIAN_INACTIVE_CODE)
}

export function concernRecordedWithoutAppointment(err) {
  // PROD-1089 step 2 (API train 366): 409 {code:'physician_inactive', findingRecorded:true} — the physician is
  //   deactivated; the concern was recorded and sent, only the appointment was refused.
  if (!!err && typeof err === 'object' && err.code === PHYSICIAN_INACTIVE_CODE) return err.findingRecorded === true
  return !!err && typeof err === 'object' && CONCERN_RECORDED_CODES.includes(err.code) && err.findingRecorded === true
}

/**
 * The follow-up line for the sent state, by the refusal code the concern was recorded
 * under (round 4, L1): no hours -> contact the office to schedule; no positive outcome
 * -> the doctor has the update and will contact the patient. Round 5: no open slot, or
 * an appointment that could not be saved -> the doctor has the update and the office
 * will contact the patient to schedule.
 */
export function concernSentMessageKey(code) { return concernSentMessageKeyFor(code) }
export function concernSentMessageKeyFor(code, hasText) {
  switch (code) {
    case PHYSICIAN_INACTIVE_CODE:
      // PROD-1089: under es (withheld) falls through to today's line
      if (textOk(hasText, PHYSICIAN_INACTIVE_CONCERN_KEY)) return PHYSICIAN_INACTIVE_CONCERN_KEY
      return 'appointment.concernSentNoBooking'
    case 'no_positive_outcome':
      return 'appointment.concernSentNoOutcome'
    case 'no_open_slot':
    case 'appointment_not_created':
      return 'appointment.concernSentOfficeWillSchedule'
    case 'doctor_has_no_hours':
    default:
      return 'appointment.concernSentNoBooking'
  }
}

/**
 * P1 round 5 (B2) — a slot conflict that wrote nothing: 409 {code:'slot_conflict',
 * conflict:true, findingRecorded:false}, or an older API's bare conflict:true. The
 * observation is still open, so the caller keeps the picker and asks for another time.
 * A conflict answer that says it recorded something is not this.
 */
export function isSlotConflict(err) {
  return !!err && typeof err === 'object' && (err.code === 'slot_conflict' || err.conflict === true) && err.findingRecorded !== true
}

/**
 * The i18n key for an API refusal the patient must read, by the contract's `code`;
 * null when the error carries none of them (the caller keeps its own message).
 */
export function appointmentErrorKey(err) { return appointmentErrorKeyFor(err) }
export function appointmentErrorKeyFor(err, hasText) {
  const code = err && typeof err === 'object' ? err.code : null
  if (isPhysicianInactive(err)) return textOk(hasText, PHYSICIAN_INACTIVE_ERR_KEY) ? PHYSICIAN_INACTIVE_ERR_KEY : null
  if (code === 'doctor_has_no_hours') return 'appointment.errDoctorHasNoHours'
  // ★ PROD-690 (2026-10-07): 409 {code:'modality_not_offered', requested, offered} — the
  //   physician does not offer the visit type the patient picked (orella-api
  //   _modality_refusal; the guards are dormant until this mapping is live). The TIME was
  //   fine, so this is not "that time was just booked": each caller shows the sentence for
  //   the modality it asked for and keeps the picked time. An unknown `requested` gets the
  //   generic "could not be created", never the slot copy.
  if (code === 'modality_not_offered') {
    if (err.requested === 'video') return 'appointment.errModalityVideoNotOffered'
    if (err.requested === 'in_person') return 'appointment.errModalityInPersonNotOffered'
    return 'appointment.errAppointmentNotCreated'
  }
  if (code === 'requested_slot_required') return 'appointment.errRequestedSlotRequired'
  if (code === 'appointment_not_created') return 'appointment.errAppointmentNotCreated'
  if (err && typeof err === 'object' && err.appointmentCreated === false) return 'appointment.errAppointmentNotCreated'
  if (code === 'slot_conflict') return 'errors2.timeSlotBooked'
  // P1 round 3 (H3): reopen_case refuses a case that is already in_follow_up
  if (code === 'already_reopened') return 'appointment.errAlreadyReopened'
  return null
}

/**
 * P1 round 5 (M1, M6) — the API refused the appointment write: a contract code, or any
 * 409 / conflict. After a refusal a caller must not write anything that says the visit
 * moved (CaseHome's follow_up JSON) or retry the write another way (a fallback create).
 */
export function appointmentRefused(err) {
  return !!appointmentErrorKey(err) || (!!err && typeof err === 'object' && (err.status === 409 || err.conflict === true))
}

/**
 * ★★★ D527 (Dr. Rami 2026-10-23, time request; handoff s116; REVERSES D-g / D-b in the app for no-hours physicians).
 * A physician who is active on Orella but has no usable hours (scheduling OFF, or ON with every weekday off:
 * reasonIsNoHours) can still receive a case. The patient REQUESTS a specific day and time; it is never booked:
 * orella-api (v243.391.0+) stores it as appointment_request.timeRequestOnly, refuses the auto-accept
 * (autoAcceptRefused: 'time_needs_confirmation') and the physician confirms or changes it in the case view.
 * The grid is the pre-D-g fallback: the next REQUEST_GRID_DAYS days from today, weekdays only (Dr. Rami 2026-10-23: Saturdays and Sundays are typically closed), 9:00-16:30 every
 * 30 minutes. ShareConversation still applies the same-day lead rule (timesAfterSameDayLead) on top.
 * ONLY for 'scheduling-off' / 'no-hours': 'none-open' (hours, all taken) and 'fetch-failed' keep NoSlotsCard.
 */
export const REQUEST_GRID_DAYS = 14
export const REQUEST_GRID_TIMES = (() => {
  const out = []
  for (let m = 9 * 60; m <= 16 * 60 + 30; m += 30) out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`)
  return out
})()

export function isTimeRequestReason(reason) {
  return reasonIsNoHours(reason)
}

/** [{date:'YYYY-MM-DD', dayName, times:[...]}] for the request calendar (local dates, never toISOString). */
export function requestGridSlots(now = new Date()) {
  const out = []
  for (let i = 0; i < REQUEST_GRID_DAYS; i++) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i)
    if (d.getDay() === 0 || d.getDay() === 6) continue
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    out.push({ date, dayName: '', times: REQUEST_GRID_TIMES.slice() })
  }
  return out
}

